/**
 * ElecBid — Jobs Tab
 * Spreadsheet-style table backed by jb_jobs. Pulls from Jobber on the
 * nightly sync; in-app edits stage locally then push back via /api/jobs-tab.
 *
 * Features:
 *   • Filter by status / lead tech / salesperson / search / date range
 *   • Inline edit on red-bucket columns + salesperson
 *   • Bulk-fill bar — pick a column, type a value, applies to all visible rows
 *   • Push-to-Jobber button for staged edits (writes customFields + salesperson)
 *   • Per-column permissions (admin only) — gear icon at each column header
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../App";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";

// ── Styles (matches InvoiceIntelligence's filter pattern) ───────────────────
const JOBS_TAB_CSS = `
  .jt-col-filter {
    position: absolute; top: calc(100% + 2px); left: 0; z-index: 300;
    background: #1a1d23; border: 1px solid rgba(255,255,255,0.12);
    border-radius: 8px; min-width: 240px; max-height: 420px;
    display: flex; flex-direction: column; box-shadow: 0 8px 24px rgba(0,0,0,0.6);
    overflow: hidden;
  }
  .jt-cf-sort { display: flex; gap: 0; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .jt-cf-sort button {
    flex: 1; background: none; border: none; cursor: pointer;
    padding: 8px 10px; font-size: 12px; color: #c0c8d4; font-family: inherit;
    border-right: 1px solid rgba(255,255,255,0.04);
  }
  .jt-cf-sort button:last-child { border-right: none; }
  .jt-cf-sort button:hover { background: rgba(255,255,255,0.04); color: #fff; }
  .jt-cf-sort button.active { color: #a5ce3a; background: rgba(165,206,58,0.08); }
  .jt-cf-section { padding: 8px 10px 6px; border-bottom: 1px solid rgba(255,255,255,0.04); }
  .jt-cf-section:last-of-type { border-bottom: none; }
  .jt-cf-label {
    font-size: 10px; color: #6b7280; margin-bottom: 4px;
    text-transform: uppercase; letter-spacing: 0.4px;
  }
  .jt-cf-input {
    width: 100%; box-sizing: border-box;
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 6px; padding: 6px 8px; color: #f0f0f0; font-size: 12px;
    outline: none; font-family: inherit;
  }
  .jt-cf-input:focus { border-color: rgba(165,206,58,0.4); }
  .jt-cf-input::placeholder { color: #4a5260; }
  .jt-cf-input::-webkit-calendar-picker-indicator { filter: invert(0.5); cursor: pointer; }
  .jt-cf-actions { display: flex; gap: 10px; align-items: center; margin-bottom: 4px; }
  .jt-cf-actions button {
    background: none; border: none; cursor: pointer;
    font-size: 11px; font-family: inherit; padding: 0;
  }
  .jt-cf-actions button:nth-of-type(1) { color: #a5ce3a; }
  .jt-cf-actions button:nth-of-type(2) { color: #6b7280; }
  .jt-cf-list {
    overflow-y: auto; max-height: 168px; margin-top: 4px;
    border: 1px solid rgba(255,255,255,0.05); border-radius: 4px;
  }
  .jt-cf-item {
    display: flex; align-items: center; gap: 8px;
    padding: 5px 8px; cursor: pointer; font-size: 12px; color: #e0e0e0;
  }
  .jt-cf-item:hover { background: rgba(255,255,255,0.04); }
  .jt-cf-item input { cursor: pointer; flex-shrink: 0; }
  .jt-cf-item span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .jt-cf-empty { padding: 8px 10px; font-size: 11px; color: #4a5260; text-align: center; }
  .jt-cf-foot {
    display: flex; gap: 6px; padding: 8px 10px;
    border-top: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;
  }
  .jt-cf-clear {
    background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2);
    color: #ef4444; padding: 6px 12px; border-radius: 6px;
    font-size: 12px; cursor: pointer; font-family: inherit;
  }
  .jt-cf-clear:hover { background: rgba(239,68,68,0.15); }
  .jt-cf-apply {
    flex: 1; background: #a5ce3a; color: #0d0f12; border: none;
    border-radius: 6px; padding: 6px 12px; font-size: 12px;
    font-weight: 700; cursor: pointer; font-family: inherit;
  }
  .jt-cf-apply:hover { background: #b8e044; }

  /* Quick filter row beneath column headers */
  .jt-quick-input {
    width: 100%; box-sizing: border-box;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 5px; padding: 4px 6px;
    color: #e0e0e0; font-size: 11px; font-family: inherit; outline: none;
  }
  .jt-quick-input:focus { border-color: rgba(165,206,58,0.4); background: rgba(255,255,255,0.07); }
  .jt-quick-input::placeholder { color: #4a5260; font-size: 10px; }
`;

// ── Theme ───────────────────────────────────────────────────────────────────
const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a303c",
  lime: "#a5ce3a", limeDim: "rgba(165,206,58,0.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,0.12)",
  red: "#ef4444", redDim: "rgba(239,68,68,0.12)",
  amber: "#f59e0b", amberDim: "rgba(245,158,11,0.12)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,0.12)",
  purple: "#a855f7", purpleDim: "rgba(168,85,247,0.12)",
  yellow: "#eab308", yellowDim: "rgba(234,179,8,0.18)",
  text: "#f0f4f8", muted: "#8892a4", dim: "#4a5568",
};

// ── Column definitions ──────────────────────────────────────────────────────
// editable: false = read-only from Jobber; true = editable + push-back
// type: 'text' | 'number' | 'date' | 'currency' | 'select' (with options) |
//       'salesperson' | 'lead_tech' | 'status'
const COLUMNS = [
  // identity
  { key: "job_num",                 label: "Job #",                 editable: false, type: "text",     w: 80 },
  { key: "client_name",             label: "Client",                editable: false, type: "text",     w: 180 },
  { key: "title",                   label: "Title",                 editable: false, type: "text",     w: 240 },
  // status mirrors Jobber's jobStatus exactly (active / today / upcoming /
  // unscheduled / late / action_required / requires_invoicing / archived).
  { key: "status",                  label: "Jobber Status",         editable: false, type: "jobber_status", w: 140 },
  // payment is computed from invoice rollup (paid / partial / unpaid / no_invoice).
  { key: "_payment",                label: "Payment",               editable: false, type: "payment",  w: 110 },
  // review is the invoice-time quote-vs-actual audit (job_audits) — grade chip
  // opens the full saved review.
  { key: "_review",                 label: "Job Review",            editable: false, type: "review",   w: 110 },
  { key: "salesperson_name",        label: "Salesperson",           editable: true,  type: "salesperson", w: 160 },
  { key: "lead_tech",               label: "Lead Tech",             editable: true,  type: "lead_tech",   w: 130 },
  { key: "service_type",            label: "Service Type",          editable: true,  type: "dropdown", w: 180 },
  { key: "additional_service_types",label: "Add'l Services",        editable: true,  type: "text",     w: 180 },
  { key: "job_type",                label: "Job Type",              editable: true,  type: "text",     w: 150 },
  { key: "job_market",              label: "Job Market",            editable: true,  type: "text",     w: 130 },
  { key: "job_complexity",          label: "Complexity",            editable: true,  type: "text",     w: 110 },

  // dates
  { key: "created_at",              label: "Created",               editable: false, type: "date",     w: 110 },
  { key: "closed_date",             label: "Closed",                editable: false, type: "date",     w: 110 },
  { key: "client_requested_date",   label: "Client Requested",      editable: true,  type: "text",     w: 130 },
  { key: "target_completion_date",  label: "Target Completion",     editable: true,  type: "text",     w: 130 },
  { key: "job_complete_last_visit", label: "Complete Last Visit",   editable: true,  type: "text",     w: 130 },

  // financials (read-only)
  { key: "total_revenue",           label: "Revenue",               editable: false, type: "currency", w: 110 },
  { key: "total_cost",              label: "Cost",                  editable: false, type: "currency", w: 110 },
  { key: "profit",                  label: "Profit",                editable: false, type: "currency", w: 110 },
  { key: "profit_pct",              label: "Profit %",              editable: false, type: "percent",  w: 80  },
  { key: "amount_invoiced",         label: "Invoiced",              editable: false, type: "currency", w: 110 },
  { key: "amount_outstanding",      label: "Outstanding",           editable: false, type: "currency", w: 110 },
  { key: "invoice_status",          label: "Invoice Status",        editable: false, type: "text",     w: 140 },

  // editable financials (quoted estimates)
  { key: "quoted_total",            label: "Quoted Total",          editable: true,  type: "text",     w: 120 },
  { key: "hours_dollar_quoted",     label: "Hours $ Quoted",        editable: true,  type: "text",     w: 120 },
  { key: "expenses_quoted",         label: "Expenses Quoted",       editable: true,  type: "text",     w: 120 },
  { key: "material_cost_quoted",    label: "Material Quoted",       editable: true,  type: "text",     w: 120 },
  { key: "rough_in_hours_quoted",   label: "Rough-in Hrs Q",        editable: true,  type: "text",     w: 100 },
  { key: "finishing_hours_quoted",  label: "Finishing Hrs Q",       editable: true,  type: "text",     w: 100 },
  { key: "van_hours_qty_quoted",    label: "Van Hrs Q",             editable: true,  type: "text",     w: 100 },
  { key: "ips_solar_materials_cost",label: "IPS Solar Cost",        editable: true,  type: "text",     w: 110 },
  { key: "sub_contractor_cost",     label: "Sub Cost Q",            editable: true,  type: "text",     w: 110 },

  // permits + meta
  { key: "po_number",               label: "PO#",                   editable: true,  type: "text",     w: 130 },
  { key: "tsbc_permit",             label: "TSBC Permit",           editable: true,  type: "text",     w: 130 },
  { key: "self_generation_app",     label: "Self Gen App",          editable: true,  type: "text",     w: 120 },
  { key: "gst_number",              label: "GST #",                 editable: true,  type: "text",     w: 130 },
  { key: "delay_cause",             label: "Delay Cause",           editable: true,  type: "text",     w: 160 },
  { key: "material_readiness",      label: "Material Readiness",    editable: true,  type: "text",     w: 140 },
  { key: "if_quoted_fill_below",    label: "If Quoted Below",       editable: true,  type: "text",     w: 140 },
];

// Jobber's raw jobStatus values, with friendly labels + colors. Mirrors what
// you see in Jobber. Anything not in this map is rendered using its raw value.
const JOBBER_STATUS_COLORS = {
  active:              { bg: C.blueDim,   fg: C.blue,   label: "Active" },
  today:               { bg: C.limeDim,   fg: C.lime,   label: "Today" },
  upcoming:            { bg: C.blueDim,   fg: C.blue,   label: "Upcoming" },
  unscheduled:         { bg: C.amberDim,  fg: C.amber,  label: "Unscheduled" },
  late:                { bg: C.redDim,    fg: C.red,    label: "Late" },
  action_required:     { bg: C.amberDim,  fg: C.amber,  label: "Action Required" },
  requires_invoicing:  { bg: C.amberDim,  fg: C.amber,  label: "Requires Invoicing" },
  archived:            { bg: C.purpleDim, fg: C.purple, label: "Archived" },
  completed:           { bg: C.greenDim,  fg: C.green,  label: "Completed" },
};
const PAYMENT_COLORS = {
  paid:           { bg: C.greenDim,  fg: C.green,  label: "Paid" },
  deposit_paid:   { bg: C.limeDim,   fg: C.lime,   label: "Deposit Paid" },
  partial:        { bg: C.amberDim,  fg: C.amber,  label: "Partial" },
  unpaid:         { bg: C.redDim,    fg: C.red,    label: "Unpaid" },
  no_invoice:     { bg: C.dim,       fg: C.muted,  label: "—" },
};

// Compute payment state from the invoice rollup columns on a job row.
// Prefer derived_status when it carries a payment-specific value (paid /
// deposit_paid / partially_paid / invoiced / bad_debt) — that's set by the
// sync's per-invoice analysis with full deposit/payment context.
function getPaymentStatus(row) {
  const ds = row.derived_status;
  if (ds === "paid") return "paid";
  if (ds === "deposit_paid") return "deposit_paid";
  if (ds === "partially_paid") return "partial";
  if (ds === "invoiced") return "unpaid";
  if (ds === "bad_debt") return "unpaid";
  // Fall back to amount-based math
  const invoiced = Number(row.amount_invoiced) || 0;
  const outstanding = Number(row.amount_outstanding) || 0;
  if (invoiced <= 0.01) return "no_invoice";
  if (outstanding <= 0.01) return "paid";
  if (outstanding < invoiced - 0.01) return "partial";
  return "unpaid";
}

// ── Formatters ──────────────────────────────────────────────────────────────
const fmtCurrency = (v) => v == null || v === "" ? "" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPercent  = (v) => v == null || v === "" ? "" : `${Number(v).toFixed(0)}%`;
const fmtDate     = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
};

function Badge({ value, palette }) {
  const cfg = palette[value] || { bg: C.dim, fg: C.muted, label: value || "—" };
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
      background: cfg.bg, color: cfg.fg, whiteSpace: "nowrap",
    }}>{cfg.label}</span>
  );
}

// ── Column visibility based on perms ────────────────────────────────────────
function isColumnVisibleToCaller(col, perms, caller) {
  if (caller?.isAdmin) return true;
  const p = perms?.[col.key];
  if (!p) return true;
  if (p.hidden) return false;
  const roles = p.allowed_roles || [];
  const emails = p.allowed_emails || [];
  if (!roles.length && !emails.length) return true; // empty perm row = visible to all
  if (caller?.email && emails.includes(caller.email)) return true;
  if (caller?.role && roles.includes(caller.role.toLowerCase())) return true;
  return false;
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════════
export default function JobsTab() {
  const session = useSession();
  const { isAdmin } = usePermissions();
  const authToken = session?.access_token || null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);              // server data
  const [edits, setEdits] = useState({});            // { [rowId]: { [col]: value } } — staged edits
  const [perms, setPerms] = useState({});            // { [column_key]: { allowed_roles, allowed_emails, hidden } }
  const [caller, setCaller] = useState({ email: null, isAdmin: false, role: null });
  const [employees, setEmployees] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  // { [column_key]: string[] } — dropdown options sourced from Jobber's
  // CustomFieldConfigurationDropdown.valueOptions. Empty array (or missing
  // key) means we have no cached options and Cell falls back to a text input.
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [refreshingDropdowns, setRefreshingDropdowns] = useState(false);
  // { [job_num]: { grade, headline, status, updated_at } } — invoice-time audits
  const [audits, setAudits] = useState({});
  const [reviewJob, setReviewJob] = useState(null); // job_num whose review modal is open

  // Filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLeadTech, setFilterLeadTech] = useState("all");
  const [filterSalesperson, setFilterSalesperson] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Bulk fill
  const [bulkColumn, setBulkColumn] = useState("");
  const [bulkValue, setBulkValue] = useState("");

  // Column perm editor
  const [permEditOpen, setPermEditOpen] = useState(null); // column_key

  // Excel-style per-column sort + filter
  // colFilters: { [key]: { values?: Set<string>, contains?: string, from?: string, to?: string } }
  // sortBy:     { key, dir: 'asc'|'desc' } | null
  const [colFilters, setColFilters] = useState({});
  const [sortBy, setSortBy] = useState(null);
  const [filterOpen, setFilterOpen] = useState(null); // column_key currently editing

  // Pagination
  const [pageSize, setPageSize] = useState(100);   // 50 | 100 | 200 | "all"
  const [page, setPage] = useState(0);             // 0-indexed

  const apiCall = useCallback(async (action, body = {}) => {
    const res = await apiFetch("/api/jobs-tab", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ action, ...body }),
    });
    const data = await res.json();
    if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  }, [authToken]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const tryLoad = async () => {
      const [jobsResp, empsResp] = await Promise.all([
        apiCall("listJobs"),
        apiCall("listEmployees"),
      ]);
      setRows(jobsResp.jobs || []);
      const permsMap = {};
      for (const p of jobsResp.perms || []) permsMap[p.column_key] = p;
      setPerms(permsMap);
      setCaller(jobsResp.caller || { email: null, isAdmin: false, role: null });
      setEmployees(empsResp.users || []);
      setLastSyncedAt(jobsResp.lastSyncedAt || null);
      setDropdownOptions(jobsResp.dropdownOptions || {});
      setAudits(jobsResp.audits || {});
    };
    try {
      try {
        await tryLoad();
      } catch (err) {
        // First-load case: jb_jobs is missing the new columns or jb_column_perms
        // doesn't exist. If the caller is admin, run the migration once and retry.
        const msg = (err.message || "").toLowerCase();
        const looksLikeMissingColumn = /column .* does not exist|relation .*jb_column_perms.* does not exist/i.test(err.message || "");
        if (looksLikeMissingColumn && isAdmin) {
          await apiCall("runMigration");
          await tryLoad();
        } else {
          throw err;
        }
      }
    } catch (err) {
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [apiCall, isAdmin]);

  // Load on mount — auth token is sent if available but not required for
  // the read/edit endpoints. Admin-only actions (column perms, migration)
  // gracefully no-op when the token is missing.
  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Visible columns (after perm filtering) ──────────────────────────────
  const visibleColumns = useMemo(
    () => COLUMNS.filter(c => isColumnVisibleToCaller(c, perms, { ...caller, isAdmin: caller.isAdmin || isAdmin })),
    [perms, caller, isAdmin]
  );

  // Resolve a row's value for a given column key — handles the synthetic
  // _payment column and falls through to row[key] otherwise.
  const valueForColumn = useCallback((row, key) => {
    if (key === "_payment") return getPaymentStatus(row);
    if (key === "_review")  return audits[String(row.job_num)]?.grade || "";
    return row[key];
  }, [audits]);

  // ── Filtered + sorted rows ──────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const fromMs = dateFrom ? new Date(dateFrom).getTime() : null;
    const toMs   = dateTo   ? new Date(dateTo).getTime() + 86400000 : null;
    const q = search.trim().toLowerCase();

    // 1. Top-bar filters + global search
    let out = rows.filter(r => {
      if (filterStatus    !== "all" && (r.status || "") !== filterStatus) return false;
      if (filterLeadTech  !== "all" && (r.lead_tech || "")        !== filterLeadTech) return false;
      if (filterSalesperson !== "all" && (r.salesperson_name || "") !== filterSalesperson) return false;
      if (q) {
        const hay = [r.job_num, r.client_name, r.title, r.lead_tech, r.salesperson_name, r.line_items_text]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (fromMs || toMs) {
        const ts = r.created_at ? new Date(r.created_at).getTime() : null;
        if (!ts) return false;
        if (fromMs && ts < fromMs) return false;
        if (toMs   && ts > toMs)   return false;
      }
      return true;
    });

    // 2. Per-column Excel-style filters
    for (const [key, f] of Object.entries(colFilters)) {
      if (!f) continue;
      const col = COLUMNS.find(c => c.key === key);
      if (!col) continue;

      // Multi-select filter (set of allowed string values)
      if (f.values && f.values.size > 0) {
        out = out.filter(r => {
          const v = valueForColumn(r, key);
          return f.values.has(v == null ? "" : String(v));
        });
      }
      // Contains (text)
      if (f.contains) {
        const needle = f.contains.toLowerCase();
        out = out.filter(r => {
          const v = valueForColumn(r, key);
          return v != null && String(v).toLowerCase().includes(needle);
        });
      }
      // Date range (only meaningful for date columns)
      if (col.type === "date" && (f.from || f.to)) {
        const fMs = f.from ? new Date(f.from).getTime() : null;
        const tMs = f.to   ? new Date(f.to).getTime() + 86400000 : null;
        out = out.filter(r => {
          const v = valueForColumn(r, key);
          if (!v) return false;
          const ts = new Date(v).getTime();
          if (isNaN(ts)) return false;
          if (fMs && ts < fMs) return false;
          if (tMs && ts > tMs) return false;
          return true;
        });
      }
    }

    // 3. Sort
    if (sortBy?.key) {
      const col = COLUMNS.find(c => c.key === sortBy.key);
      const dir = sortBy.dir === "desc" ? -1 : 1;
      const isNumeric = col && (col.type === "currency" || col.type === "percent");
      const isDate    = col && col.type === "date";
      out = [...out].sort((a, b) => {
        const va = valueForColumn(a, sortBy.key);
        const vb = valueForColumn(b, sortBy.key);
        const aNull = va == null || va === "";
        const bNull = vb == null || vb === "";
        if (aNull && bNull) return 0;
        if (aNull) return 1;   // nulls always last
        if (bNull) return -1;
        if (isDate)         return dir * (new Date(va).getTime() - new Date(vb).getTime());
        if (isNumeric)      return dir * (Number(va) - Number(vb));
        // job_num is numeric-ish (string of digits) — compare as numbers when both parse
        if (sortBy.key === "job_num") {
          const na = Number(va), nb = Number(vb);
          if (!isNaN(na) && !isNaN(nb)) return dir * (na - nb);
        }
        return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
      });
    }

    return out;
  }, [rows, filterStatus, filterLeadTech, filterSalesperson, search, dateFrom, dateTo, colFilters, sortBy, valueForColumn]);

  // ── Paginated rows (slice from filteredRows) ────────────────────────────
  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = useMemo(() => {
    if (pageSize === "all") return filteredRows;
    const start = safePage * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, pageSize, safePage]);

  // Reset to page 0 whenever filters change (so user doesn't end up on an
  // empty page after narrowing results).
  useEffect(() => { setPage(0); }, [filterStatus, filterLeadTech, filterSalesperson, search, dateFrom, dateTo, colFilters, sortBy, pageSize]);

  // ── Cell value: edits override server data ──────────────────────────────
  const getCellValue = (row, key) => {
    const e = edits[row.id]?.[key];
    if (e !== undefined) return e;
    if (key === "salesperson_name") return row.salesperson_name;
    return row[key];
  };

  const isCellDirty = (row, key) => edits[row.id]?.[key] !== undefined;

  const setCellValue = (rowId, key, value) => {
    setEdits(prev => {
      const next = { ...prev };
      const rowEdits = { ...(next[rowId] || {}) };
      // Compare to server value to detect "edited back to original"
      const row = rows.find(r => r.id === rowId);
      const serverVal = row ? (row[key] ?? null) : null;
      if (value == null || value === "" ? serverVal == null || serverVal === "" : String(value) === String(serverVal)) {
        delete rowEdits[key];
      } else {
        rowEdits[key] = value;
      }
      if (Object.keys(rowEdits).length === 0) delete next[rowId];
      else next[rowId] = rowEdits;
      return next;
    });
  };

  const totalDirtyCells = useMemo(
    () => Object.values(edits).reduce((sum, r) => sum + Object.keys(r).length, 0),
    [edits]
  );

  // ── Apply bulk fill to all currently filtered rows ─────────────────────
  const applyBulkFill = () => {
    if (!bulkColumn || bulkValue === "") return;
    const col = COLUMNS.find(c => c.key === bulkColumn);
    if (!col?.editable) return;
    const newEdits = { ...edits };
    for (const r of filteredRows) {
      const serverVal = r[bulkColumn] ?? null;
      const targetVal = bulkValue;
      if (String(serverVal ?? "") === String(targetVal)) continue;
      const rowEdits = { ...(newEdits[r.id] || {}) };
      rowEdits[bulkColumn] = targetVal;
      newEdits[r.id] = rowEdits;
    }
    setEdits(newEdits);
  };

  // ── Save edits (Supabase only) ─────────────────────────────────────────
  const saveLocal = async () => {
    const list = [];
    for (const [id, cols] of Object.entries(edits)) {
      for (const [column, value] of Object.entries(cols)) {
        list.push({ id: Number(id), column: columnToApiName(column), value: valueForApi(column, value) });
      }
    }
    if (!list.length) return;
    await apiCall("saveEdits", { edits: list });
    // mirror into local rows so they persist on refresh
    setRows(prev => prev.map(r => {
      const e = edits[r.id];
      if (!e) return r;
      const next = { ...r };
      for (const [k, v] of Object.entries(e)) {
        if (k === "salesperson_name") {
          next.salesperson_name = v?.name || (typeof v === "string" ? v : null);
          next.salesperson_id   = v?.id   || null;
        } else next[k] = v;
      }
      return next;
    }));
    setEdits({});
  };

  // ── Refresh dropdown options from Jobber ───────────────────────────────
  // Admin-only one-shot. Pulls Jobber's CustomFieldConfigurationDropdown
  // valueOptions for every known dropdown column and caches them in
  // jb_cf_dropdown_options. Surfaces a toast on completion.
  const refreshDropdownOptions = async () => {
    setRefreshingDropdowns(true);
    setPushResult(null);
    try {
      const data = await apiCall("refreshDropdownOptions");
      const failed = (data.results || []).filter(r => !r.ok);
      setPushResult({
        ok: true,
        pushed: data.refreshed || 0,
        failed: failed.length,
        results: failed.map(r => ({ ok: false, error: `${r.column_key}: ${r.error}` })),
        kind: "dropdownRefresh",
      });
      await loadAll();
    } catch (err) {
      setPushResult({ ok: false, error: err.message, kind: "dropdownRefresh" });
    } finally {
      setRefreshingDropdowns(false);
    }
  };

  // ── Push edits to Jobber ───────────────────────────────────────────────
  const pushToJobber = async () => {
    setPushing(true);
    setPushResult(null);
    try {
      const list = [];
      for (const [id, cols] of Object.entries(edits)) {
        for (const [column, value] of Object.entries(cols)) {
          list.push({ id: Number(id), column: columnToApiName(column), value: valueForApi(column, value) });
        }
      }
      if (!list.length) { setPushing(false); return; }
      const data = await apiCall("pushEditsToJobber", { edits: list });
      setPushResult(data);
      // On success, clear edits and reload
      if (data.failed === 0) {
        setEdits({});
        await loadAll();
      }
    } catch (err) {
      setPushResult({ ok: false, error: err.message });
    } finally {
      setPushing(false);
    }
  };

  // ── Sync health: how stale is the most recent jb_jobs.last_synced_at ───
  // Cron runs nightly at 7 AM, so > 36 h means it skipped at least once.
  // > 7 d means something is genuinely broken — surface a stronger warning.
  const syncHealth = useMemo(() => {
    if (!lastSyncedAt) return { level: "unknown", label: "No sync data yet — Jobber connection may not be set up." };
    const ageMs = Date.now() - new Date(lastSyncedAt).getTime();
    const hours = ageMs / 3_600_000;
    const ago = hours < 1 ? "just now"
      : hours < 24 ? `${Math.round(hours)} h ago`
      : `${Math.round(hours / 24)} d ago`;
    if (hours > 168) return { level: "error",   label: `Last Jobber sync ${ago}. Jobs may be very out of date — check the Jobber connection in Admin.` };
    if (hours > 36)  return { level: "warning", label: `Last Jobber sync ${ago}. Nightly cron may have skipped — re-run from Admin if this persists.` };
    return { level: "ok", label: `Last Jobber sync ${ago}.` };
  }, [lastSyncedAt]);

  // ── Lead tech / salesperson distinct lists for filters ─────────────────
  const distinctLeadTechs = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.lead_tech) set.add(r.lead_tech);
    return [...set].sort();
  }, [rows]);

  const distinctSalespersons = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.salesperson_name) set.add(r.salesperson_name);
    return [...set].sort();
  }, [rows]);

  // ────────────────────────────────────────────────────────────────────────
  if (loading) return <div style={{ padding: 24, color: C.muted }}>Loading jobs…</div>;
  if (error)   return <div style={{ padding: 24, color: C.red }}>Error: {error}</div>;

  return (
    <div style={{ padding: "16px 20px", color: C.text, background: C.bg, minHeight: "calc(100vh - 60px)" }}>
      <style>{JOBS_TAB_CSS}</style>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Jobs</h1>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
            {filteredRows.length.toLocaleString()} of {rows.length.toLocaleString()} jobs
            {totalDirtyCells > 0 && <> · <span style={{ color: C.yellow }}>{totalDirtyCells} unsaved edit{totalDirtyCells !== 1 ? "s" : ""}</span></>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {totalDirtyCells > 0 && (
            <>
              <button onClick={() => setEdits({})} style={btnStyle("ghost")}>Discard</button>
              <button onClick={saveLocal} style={btnStyle("secondary")}>Save in ElecBid</button>
              <button onClick={pushToJobber} disabled={pushing} style={btnStyle("primary")}>
                {pushing ? "Pushing…" : `Push ${totalDirtyCells} to Jobber`}
              </button>
            </>
          )}
          {(Object.keys(colFilters).length > 0 || sortBy) && (
            <button onClick={() => { setColFilters({}); setSortBy(null); }} style={btnStyle("ghost")} title="Clear all column filters and sort">
              Clear filters
            </button>
          )}
          {(caller.isAdmin || isAdmin) && (
            <button
              onClick={refreshDropdownOptions}
              disabled={refreshingDropdowns}
              style={btnStyle(refreshingDropdowns ? "ghost" : "secondary")}
              title="Pull dropdown options (Service Type, Job Type, etc.) from Jobber"
            >
              {refreshingDropdowns ? "Refreshing…" : "Refresh dropdown options"}
            </button>
          )}
          <button onClick={loadAll} style={btnStyle("ghost")}>Refresh</button>
        </div>
      </div>

      {/* Passive sync-health banner — only shown when it's actually a problem */}
      {(syncHealth.level === "warning" || syncHealth.level === "error" || syncHealth.level === "unknown") && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 12,
          background: syncHealth.level === "error" ? C.redDim : C.amberDim,
          color:      syncHealth.level === "error" ? C.red    : C.amber,
          fontSize: 13, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 16 }}>{syncHealth.level === "error" ? "⚠" : "⚠"}</span>
          {syncHealth.label}
        </div>
      )}

      {/* Push result toast */}
      {pushResult && (
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 12,
          background: pushResult.failed === 0 ? C.greenDim : C.amberDim,
          color: pushResult.failed === 0 ? C.green : C.amber,
          fontSize: 13,
        }}>
          {pushResult.error
            ? `Error: ${pushResult.error}`
            : pushResult.kind === "dropdownRefresh"
              ? (pushResult.failed === 0
                  ? `Refreshed ${pushResult.pushed} dropdown column${pushResult.pushed !== 1 ? "s" : ""} from Jobber.`
                  : `Refreshed ${pushResult.pushed}, ${pushResult.failed} failed. ${pushResult.results?.filter(r => !r.ok).slice(0, 3).map(r => r.error).join(" · ")}`)
              : pushResult.failed === 0
                ? `Pushed ${pushResult.pushed} job${pushResult.pushed !== 1 ? "s" : ""} to Jobber.`
                : `Pushed ${pushResult.pushed}, failed ${pushResult.failed}. ${pushResult.results?.filter(r => !r.ok).slice(0, 3).map(r => r.error).join(" · ")}`}
          {pushResult.results?.some(r => r.salespersonRejected) && (
            <div style={{ marginTop: 4, fontSize: 11 }}>
              Note: Jobber's API does not allow setting salesperson via this endpoint. Salesperson edits saved in ElecBid only.
            </div>
          )}
          <button onClick={() => setPushResult(null)} style={{ float: "right", background: "transparent", border: "none", color: "inherit", cursor: "pointer" }}>×</button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, padding: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <input
          type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: 220, flex: 1 }}
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inputStyle}>
          <option value="all" style={optionStyle}>All Jobber statuses</option>
          {Object.entries(JOBBER_STATUS_COLORS).map(([k, v]) => <option key={k} value={k} style={optionStyle}>{v.label}</option>)}
        </select>
        <select value={filterLeadTech} onChange={e => setFilterLeadTech(e.target.value)} style={inputStyle}>
          <option value="all" style={optionStyle}>All lead techs</option>
          {distinctLeadTechs.map(n => <option key={n} value={n} style={optionStyle}>{n}</option>)}
        </select>
        <select value={filterSalesperson} onChange={e => setFilterSalesperson(e.target.value)} style={inputStyle}>
          <option value="all" style={optionStyle}>All salespeople</option>
          {distinctSalespersons.map(n => <option key={n} value={n} style={optionStyle}>{n}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} title="Created from" />
        <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   style={inputStyle} title="Created to" />
      </div>

      {/* Bulk fill */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12, padding: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>Bulk fill ({filteredRows.length} visible rows):</span>
        <select value={bulkColumn} onChange={e => setBulkColumn(e.target.value)} style={inputStyle}>
          <option value="" style={optionStyle}>Pick a column…</option>
          {COLUMNS.filter(c => c.editable && isColumnVisibleToCaller(c, perms, { ...caller, isAdmin: caller.isAdmin || isAdmin })).map(c => (
            <option key={c.key} value={c.key} style={optionStyle}>{c.label}</option>
          ))}
        </select>
        {bulkColumn === "lead_tech" || bulkColumn === "salesperson_name" ? (
          <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={inputStyle}>
            <option value="" style={optionStyle}>— select —</option>
            {employees.map(u => <option key={u.id} value={u.name} style={optionStyle}>{u.name}</option>)}
          </select>
        ) : (COLUMNS.find(c => c.key === bulkColumn)?.type === "dropdown" && (dropdownOptions[bulkColumn]?.length || 0) > 0) ? (
          <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={inputStyle}>
            <option value="" style={optionStyle}>— select —</option>
            {dropdownOptions[bulkColumn].map(o => <option key={o} value={o} style={optionStyle}>{o}</option>)}
          </select>
        ) : (
          <input type="text" placeholder="Value to apply" value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...inputStyle, minWidth: 220 }} />
        )}
        <button onClick={applyBulkFill} disabled={!bulkColumn || bulkValue === ""} style={btnStyle(bulkColumn && bulkValue !== "" ? "secondary" : "ghost")}>
          Apply to {filteredRows.length} rows
        </button>
      </div>

      {/* Table — height-bounded so the horizontal scrollbar stays in viewport */}
      <div style={{
        overflow: "auto",
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        background: C.surface,
        maxHeight: "calc(100vh - 280px)",
        WebkitOverflowScrolling: "touch",
      }}>
        <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: "100%" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <tr>
              {visibleColumns.map(col => {
                const hasFilter = !!colFilters[col.key];
                const isSorted = sortBy?.key === col.key;
                const isActive = hasFilter || isSorted;
                return (
                  <th key={col.key} style={{
                    padding: "8px 10px", textAlign: "left",
                    borderBottom: `1px solid ${C.border}`,
                    borderRight: `1px solid ${C.border}`,
                    fontWeight: 600,
                    color: isActive ? C.lime : C.muted,
                    // Solid background — sticky headers need to be opaque or
                    // scrolling rows show through the transparent tint.
                    background: C.card,
                    fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4,
                    whiteSpace: "nowrap", minWidth: col.w, width: col.w, position: "relative",
                    userSelect: "none",
                  }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                      <span
                        onClick={() => setSortBy(isSorted && sortBy.dir === "asc"
                          ? { key: col.key, dir: "desc" }
                          : isSorted && sortBy.dir === "desc"
                            ? null
                            : { key: col.key, dir: "asc" })}
                        style={{ cursor: "pointer" }}
                        title="Click to sort"
                      >
                        {col.label}
                        {col.editable && <span style={{ color: C.lime, marginLeft: 4, fontSize: 10, textTransform: "none" }}>✎</span>}
                        {isSorted && <span style={{ marginLeft: 3, fontSize: 9 }}>{sortBy.dir === "asc" ? "▲" : "▼"}</span>}
                      </span>
                      <span
                        onClick={(e) => { e.stopPropagation(); setFilterOpen(filterOpen === col.key ? null : col.key); }}
                        title={hasFilter ? "Filter active — click to edit" : "Filter"}
                        style={{
                          cursor: "pointer", fontSize: 14, lineHeight: 1,
                          color: hasFilter ? C.lime : C.muted,
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: 22, height: 22, borderRadius: "50%",
                          background: hasFilter ? "rgba(165,206,58,0.18)"
                                    : filterOpen === col.key ? "rgba(255,255,255,0.1)" : "transparent",
                          marginLeft: 2, flexShrink: 0,
                        }}
                      >⏷</span>
                      {(caller.isAdmin || isAdmin) && (
                        <span
                          onClick={(e) => { e.stopPropagation(); setPermEditOpen(col.key); }}
                          title="Column visibility"
                          style={{
                            cursor: "pointer", fontSize: 11, lineHeight: 1,
                            color: perms[col.key] ? C.amber : C.dim,
                            marginLeft: 2,
                          }}
                        >⚙</span>
                      )}
                    </div>
                    {filterOpen === col.key && (
                      <ColumnFilterPopover
                        col={col}
                        rows={rows}
                        valueForColumn={valueForColumn}
                        currentFilter={colFilters[col.key]}
                        currentSort={isSorted ? sortBy : null}
                        onChange={(filter) => setColFilters(prev => {
                          const next = { ...prev };
                          if (!filter) delete next[col.key]; else next[col.key] = filter;
                          return next;
                        })}
                        onSort={(dir) => setSortBy(dir ? { key: col.key, dir } : null)}
                        onClose={() => setFilterOpen(null)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
            {/* Quick filter row — always-visible single-input per column for fast contains-search */}
            <tr>
              {visibleColumns.map(col => {
                const f = colFilters[col.key] || {};
                const quickValue = f.contains || "";
                return (
                  <th key={col.key} style={{
                    padding: "4px 6px", borderBottom: `1px solid ${C.border}`,
                    borderRight: `1px solid ${C.border}`,
                    background: C.surface,
                    minWidth: col.w, width: col.w,
                  }}>
                    <input
                      type={col.type === "date" ? "date" : "text"}
                      className="jt-quick-input"
                      placeholder={col.type === "date" ? "from…" : "filter…"}
                      value={col.type === "date" ? (f.from || "") : quickValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        setColFilters(prev => {
                          const next = { ...prev };
                          const cur = { ...(next[col.key] || {}) };
                          if (col.type === "date") {
                            if (v) cur.from = v; else delete cur.from;
                          } else {
                            if (v) cur.contains = v; else delete cur.contains;
                          }
                          if (Object.keys(cur).length === 0) delete next[col.key];
                          else next[col.key] = cur;
                          return next;
                        });
                      }}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map(row => (
              <tr key={row.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                {visibleColumns.map(col => (
                  <Cell
                    key={col.key}
                    row={row}
                    col={col}
                    value={getCellValue(row, col.key)}
                    dirty={isCellDirty(row, col.key)}
                    onChange={(v) => setCellValue(row.id, col.key, v)}
                    employees={employees}
                    dropdownOptions={dropdownOptions[col.key]}
                    audit={audits[String(row.job_num)] || null}
                    onOpenReview={() => setReviewJob(String(row.job_num))}
                  />
                ))}
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr><td colSpan={visibleColumns.length} style={{ padding: 40, textAlign: "center", color: C.muted }}>No jobs match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.muted }}>
          <span>Show</span>
          <select value={pageSize} onChange={e => setPageSize(e.target.value === "all" ? "all" : Number(e.target.value))} style={inputStyle}>
            <option value={50} style={optionStyle}>50</option>
            <option value={100} style={optionStyle}>100</option>
            <option value={200} style={optionStyle}>200</option>
            <option value="all" style={optionStyle}>All</option>
          </select>
          <span>per page</span>
          {pageSize !== "all" && filteredRows.length > 0 && (
            <span style={{ marginLeft: 12 }}>
              {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, filteredRows.length)} of {filteredRows.length}
            </span>
          )}
          {pageSize === "all" && (
            <span style={{ marginLeft: 12 }}>showing all {filteredRows.length}</span>
          )}
        </div>
        {pageSize !== "all" && totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button onClick={() => setPage(0)} disabled={safePage === 0} style={btnStyle(safePage === 0 ? "ghost" : "secondary")}>« First</button>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0} style={btnStyle(safePage === 0 ? "ghost" : "secondary")}>‹ Prev</button>
            <span style={{ fontSize: 12, color: C.muted, padding: "0 8px" }}>Page {safePage + 1} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} style={btnStyle(safePage >= totalPages - 1 ? "ghost" : "secondary")}>Next ›</button>
            <button onClick={() => setPage(totalPages - 1)} disabled={safePage >= totalPages - 1} style={btnStyle(safePage >= totalPages - 1 ? "ghost" : "secondary")}>Last »</button>
          </div>
        )}
      </div>

      {/* Job review modal */}
      {reviewJob && (
        <JobReviewModal
          jobNumber={reviewJob}
          summary={audits[reviewJob] || null}
          apiCall={apiCall}
          onClose={() => setReviewJob(null)}
        />
      )}

      {/* Column perm editor modal */}
      {permEditOpen && (
        <ColumnPermEditor
          columnKey={permEditOpen}
          columnLabel={COLUMNS.find(c => c.key === permEditOpen)?.label || permEditOpen}
          existing={perms[permEditOpen] || null}
          employees={employees}
          onClose={() => setPermEditOpen(null)}
          onSave={async (perm) => {
            await apiCall("saveColumnPerm", perm);
            setPerms(prev => ({ ...prev, [permEditOpen]: { ...perm, column_key: permEditOpen } }));
            setPermEditOpen(null);
          }}
        />
      )}
    </div>
  );
}

// ── Cell renderer ──────────────────────────────────────────────────────────
function Cell({ row, col, value, dirty, onChange, employees, dropdownOptions, audit, onOpenReview }) {
  const navigate = useNavigate();
  const cellBase = {
    padding: "6px 10px",
    borderRight: `1px solid ${C.border}`,
    background: dirty ? C.yellowDim : "transparent",
    whiteSpace: "nowrap",
    minWidth: col.w, width: col.w,
    color: dirty ? C.text : C.text,
  };

  if (col.type === "review") {
    if (!audit) return <td style={cellBase}><span style={{ color: C.dim }}>—</span></td>;
    const g = String(audit.grade || "?");
    const gc = g.startsWith("A") ? C.green : g.startsWith("B") ? C.lime : g.startsWith("C") ? C.amber : C.red;
    return (
      <td style={cellBase}>
        <button
          onClick={onOpenReview}
          title={audit.headline || "Open job review"}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
            background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`,
            borderRadius: 999, padding: "2px 10px", fontFamily: "inherit",
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 800, color: gc }}>{g}</span>
          <span style={{ fontSize: 11, color: C.muted }}>📋 review</span>
        </button>
      </td>
    );
  }

  // Job # opens the per-job workspace (/job/:jobNum) — visits, expenses,
  // invoices, notes, plus the pipeline FlowStrip back to quote/assessment.
  if (col.key === "job_num") {
    return (
      <td style={cellBase}>
        <span
          onClick={() => value != null && navigate(`/job/${value}`)}
          title="Open job workspace"
          style={{ color: C.lime, fontWeight: 700, cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(165,206,58,.35)" }}
        >
          {value ?? ""}
        </span>
      </td>
    );
  }

  if (!col.editable) {
    let display;
    if (col.type === "currency")           display = fmtCurrency(value);
    else if (col.type === "percent")       display = fmtPercent(value);
    else if (col.type === "date")          display = fmtDate(value);
    else if (col.type === "jobber_status") display = <Badge value={value} palette={JOBBER_STATUS_COLORS} />;
    else if (col.type === "payment")       display = <Badge value={getPaymentStatus(row)} palette={PAYMENT_COLORS} />;
    else                                   display = value ?? "";
    return <td style={cellBase}>{display}</td>;
  }

  // Editable cells
  if (col.type === "salesperson") {
    const currentName = typeof value === "object" ? value?.name : value;
    return (
      <td style={cellBase}>
        <select
          value={currentName || ""}
          onChange={(e) => {
            const name = e.target.value;
            const u = employees.find(x => x.name === name);
            onChange(name ? { id: u?.id || null, name } : null);
          }}
          style={editInputStyle(dirty)}
        >
          <option value="" style={optionStyle}>—</option>
          {employees.map(u => <option key={u.id} value={u.name} style={optionStyle}>{u.name}</option>)}
        </select>
      </td>
    );
  }

  if (col.type === "lead_tech") {
    return (
      <td style={cellBase}>
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          style={editInputStyle(dirty)}
        >
          <option value="" style={optionStyle}>—</option>
          {employees.map(u => <option key={u.id} value={u.name.split(" ")[0]} style={optionStyle}>{u.name}</option>)}
        </select>
      </td>
    );
  }

  if (col.type === "dropdown") {
    const opts = Array.isArray(dropdownOptions) ? dropdownOptions : [];
    // Current value may be a historical free-text entry that's no longer
    // (or never was) in Jobber's option list. Include it so we don't
    // silently drop it from the cell — but mark it so it's obvious.
    const v = value == null ? "" : String(value);
    const knownSet = new Set(opts);
    const showOrphan = v && !knownSet.has(v);
    // If options haven't been refreshed yet, fall back to a plain text input
    // so the field is still editable.
    if (!opts.length) {
      return (
        <td style={cellBase}>
          <input
            type="text"
            value={v}
            onChange={(e) => onChange(e.target.value)}
            placeholder="(run Refresh dropdown options)"
            style={editInputStyle(dirty)}
          />
        </td>
      );
    }
    return (
      <td style={cellBase}>
        <select
          value={v}
          onChange={(e) => onChange(e.target.value || null)}
          style={editInputStyle(dirty)}
        >
          <option value="" style={optionStyle}>—</option>
          {showOrphan && <option value={v} style={optionStyle}>{v} (legacy)</option>}
          {opts.map(o => <option key={o} value={o} style={optionStyle}>{o}</option>)}
        </select>
      </td>
    );
  }

  return (
    <td style={cellBase}>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        style={editInputStyle(dirty)}
      />
    </td>
  );
}

// ── Per-column sort + filter popover ──────────────────────────────────────
function ColumnFilterPopover({ col, rows, valueForColumn, currentFilter, currentSort, onChange, onSort, onClose }) {
  const [contains, setContains] = useState(currentFilter?.contains || "");
  const [from, setFrom]         = useState(currentFilter?.from || "");
  const [to, setTo]             = useState(currentFilter?.to || "");
  // selected: Set of value strings the user wants to keep (empty Set = filter
  // hides everything; null = no value-filter applied)
  const initialSelected = currentFilter?.values ? new Set(currentFilter.values) : null;
  const [selected, setSelected] = useState(initialSelected);
  const [valueSearch, setValueSearch] = useState("");

  // Distinct values present in the data for this column
  const distinctValues = useMemo(() => {
    const set = new Set();
    for (const r of rows) {
      const v = valueForColumn(r, col.key);
      set.add(v == null || v === "" ? "" : String(v));
    }
    return [...set].sort((a, b) => {
      // Empty/blank last
      if (!a && b) return 1;
      if (a && !b) return -1;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
    });
  }, [rows, col.key, valueForColumn]);

  const filteredValues = useMemo(() => {
    if (!valueSearch.trim()) return distinctValues;
    const q = valueSearch.toLowerCase();
    return distinctValues.filter(v => (v || "(blank)").toLowerCase().includes(q));
  }, [distinctValues, valueSearch]);

  const toggle = (v) => {
    setSelected(prev => {
      const next = new Set(prev || distinctValues);
      if (next.has(v)) next.delete(v);
      else             next.add(v);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(distinctValues));
  const selectNone = () => setSelected(new Set());

  const apply = () => {
    const f = {};
    if (contains.trim())        f.contains = contains.trim();
    if (col.type === "date") {
      if (from) f.from = from;
      if (to)   f.to   = to;
    }
    if (selected && selected.size > 0 && selected.size < distinctValues.length) {
      f.values = selected;
    }
    onChange(Object.keys(f).length ? f : null);
    onClose();
  };
  const clear = () => {
    setSelected(null);
    setContains("");
    setFrom(""); setTo("");
    onChange(null);
    onSort(null);
    onClose();
  };

  const isCheckbox = (v) => selected ? selected.has(v) : true;

  return (
    <>
      {/* click-outside scrim */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 50 }} />
      <div onClick={e => e.stopPropagation()} className="jt-col-filter">
        {/* Sort row */}
        <div className="jt-cf-sort">
          <button
            onClick={() => { onSort("asc");  onClose(); }}
            className={currentSort?.dir === "asc" ? "active" : ""}
          >▲ Sort A→Z</button>
          <button
            onClick={() => { onSort("desc"); onClose(); }}
            className={currentSort?.dir === "desc" ? "active" : ""}
          >▼ Sort Z→A</button>
        </div>

        {/* Date range — only for date columns */}
        {col.type === "date" && (
          <div className="jt-cf-section">
            <div className="jt-cf-label">Date range</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input type="date" className="jt-cf-input" value={from} onChange={e => setFrom(e.target.value)} />
              <input type="date" className="jt-cf-input" value={to}   onChange={e => setTo(e.target.value)} />
            </div>
          </div>
        )}

        {/* Contains */}
        <div className="jt-cf-section">
          <div className="jt-cf-label">Contains</div>
          <input type="text" className="jt-cf-input" value={contains} onChange={e => setContains(e.target.value)} placeholder="search text…" />
        </div>

        {/* Multi-select values list */}
        <div className="jt-cf-section">
          <div className="jt-cf-actions">
            <span className="jt-cf-label" style={{ flex: 1 }}>Show only</span>
            <button onClick={selectAll}>Select all</button>
            <button onClick={selectNone}>Clear all</button>
          </div>
          <input type="text" className="jt-cf-input" value={valueSearch} onChange={e => setValueSearch(e.target.value)} placeholder="filter values…" />
          <div className="jt-cf-list">
            {filteredValues.slice(0, 200).map(v => (
              <label key={v} className="jt-cf-item">
                <input type="checkbox" checked={isCheckbox(v)} onChange={() => toggle(v)} />
                <span>{v || <em style={{ color: "#4a5260" }}>(blank)</em>}</span>
              </label>
            ))}
            {filteredValues.length === 0 && <div className="jt-cf-empty">No matches</div>}
            {filteredValues.length > 200 && (
              <div className="jt-cf-empty">…{filteredValues.length - 200} more — narrow search</div>
            )}
          </div>
        </div>

        <div className="jt-cf-foot">
          <button className="jt-cf-clear" onClick={clear}>Clear</button>
          <button className="jt-cf-apply" onClick={apply}>Apply</button>
        </div>
      </div>
    </>
  );
}

// ── Per-column permission editor ──────────────────────────────────────────
function ColumnPermEditor({ columnKey, columnLabel, existing, employees, onClose, onSave }) {
  const [hidden, setHidden]                 = useState(!!existing?.hidden);
  const [allowedRoles, setAllowedRoles]     = useState(existing?.allowed_roles || []);
  const [allowedEmails, setAllowedEmails]   = useState(existing?.allowed_emails || []);
  const ROLES = ["owner", "admin", "ops lead tech", "journeyman", "apprentice"];

  const toggleRole = (r) => setAllowedRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  const toggleEmail = (e) => setAllowedEmails(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: 20, maxWidth: 500, width: "100%", maxHeight: "80vh", overflowY: "auto", color: C.text,
      }}>
        <h3 style={{ margin: 0, marginBottom: 4, fontSize: 16 }}>Column visibility — {columnLabel}</h3>
        <p style={{ fontSize: 12, color: C.muted, margin: "0 0 16px 0" }}>
          Admins always see all columns. With nothing set, everyone sees this column.
          Pick roles or specific people to restrict who can see it. Toggle "hide entirely" to keep it admins-only.
        </p>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, fontSize: 13 }}>
          <input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} />
          Hide from non-admins entirely
        </label>

        <div style={{ marginBottom: 16, opacity: hidden ? 0.4 : 1 }}>
          <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 6 }}>Allowed roles</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {ROLES.map(r => (
              <button key={r} onClick={() => toggleRole(r)} disabled={hidden} style={{
                ...btnStyle(allowedRoles.includes(r) ? "secondary" : "ghost"),
                fontSize: 12, padding: "4px 10px", textTransform: "capitalize",
              }}>{r}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16, opacity: hidden ? 0.4 : 1 }}>
          <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 6 }}>Allowed individuals</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 200, overflowY: "auto" }}>
            {employees.filter(u => u.email).map(u => (
              <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "4px 6px", cursor: "pointer" }}>
                <input type="checkbox" checked={allowedEmails.includes(u.email)} disabled={hidden} onChange={() => toggleEmail(u.email)} />
                {u.name} <span style={{ color: C.muted, fontSize: 11 }}>({u.email})</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btnStyle("ghost")}>Cancel</button>
          <button onClick={() => onSave({ allowed_roles: allowedRoles, allowed_emails: allowedEmails, hidden })} style={btnStyle("primary")}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
// Some columns are the salesperson_name / salesperson_id pair; the API handles
// both via a single { column: 'salesperson', value: { id, name } } edit.
function columnToApiName(col) {
  if (col === "salesperson_name") return "salesperson";
  return col;
}
function valueForApi(col, value) {
  if (col === "salesperson_name" && value && typeof value === "object") return value;
  return value;
}

// ── Job review modal — the saved invoice-time quote-vs-actual audit ─────────
function JobReviewModal({ jobNumber, summary, apiCall, onClose }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiCall("getJobAudit", { jobNumber });
        if (alive) setAudit(r.audit || null);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load review");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiCall, jobNumber]);

  const g = String(audit?.grade || summary?.grade || "?");
  const gc = g.startsWith("A") ? C.green : g.startsWith("B") ? C.lime : g.startsWith("C") ? C.amber : C.red;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
          width: "min(720px, 96vw)", maxHeight: "86vh", display: "flex", flexDirection: "column",
          boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: gc }}>{g}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
              Job #{jobNumber} review{audit?.client_name ? ` — ${audit.client_name}` : ""}
            </div>
            <div style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {audit?.headline || summary?.headline || ""}
            </div>
          </div>
          <button onClick={onClose} style={btnStyle("ghost")}>✕ Close</button>
        </div>
        <div style={{ overflowY: "auto", padding: 18 }}>
          {loading && <div style={{ color: C.muted, fontSize: 13 }}>Loading review…</div>}
          {error && <div style={{ color: C.red, fontSize: 13 }}>{error}</div>}
          {!loading && !error && !audit && (
            <div style={{ color: C.muted, fontSize: 13 }}>No saved review for this job yet.</div>
          )}
          {audit?.note_text && (
            <pre style={{
              whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
              fontFamily: "inherit", fontSize: 13, lineHeight: 1.55, color: C.text,
            }}>{audit.note_text}</pre>
          )}
          {audit && (
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.dim }}>
              quote #{audit.quote_num || "—"} · invoices {audit.invoice_nums || "—"} · updated {fmtDate(audit.updated_at)}
              {audit.note_id ? " · an older copy is still a note on the Jobber job" : " · kept in ElecBid only (not posted to Jobber)"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Inline styles ─────────────────────────────────────────────────────────
const inputStyle = {
  padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
  background: C.bg, color: C.text, fontSize: 12,
};
const editInputStyle = (dirty) => ({
  width: "100%", padding: "2px 4px",
  background: dirty ? C.yellowDim : "transparent",
  border: dirty ? `1px solid ${C.yellow}` : `1px solid transparent`,
  color: C.text, fontSize: 12, fontFamily: "inherit", borderRadius: 4,
  outline: "none",
});
// Explicit option styling so the dropdown popup is readable on Chrome/Windows
// (otherwise transparent <select> backgrounds bleed through and options become
// light-on-light).
const optionStyle = { background: C.surface, color: C.text };
function btnStyle(variant) {
  const base = {
    padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer",
    fontSize: 12, fontWeight: 600, transition: "all 0.15s",
  };
  if (variant === "primary")   return { ...base, background: C.lime, color: "#000" };
  if (variant === "secondary") return { ...base, background: C.blue, color: "#fff" };
  return { ...base, background: "transparent", color: C.muted, border: `1px solid ${C.border}` };
}
